import { useState, useEffect, useCallback } from "react";
import {
  ReactFlow,
  Controls,
  Background,
  addEdge,
  Panel,
  MiniMap,
} from "reactflow";
import "reactflow/dist/style.css";
import { CircularProgress } from "@material-ui/core";

import { nodeTypes } from "./types/nodeTypes";
import { edgeTypes } from "./components/buttonAdd/Edges";
import { fetchData } from "./services/fetchData";
import { ThemeSelector } from "./components/theme";
import { useFlowNodes } from "./context/FlowNodesProvider";
import { fetchIntegrations } from "./services/fetchIntegrations";
import SidebarFlow from "./components/Sidebar";

const getFirstChildNode = (nodes, parentId) => {
  return (
    nodes.find(
      (e) =>
        e.data?.parentData?.id === parentId &&
        (e.type === "extensionConfigIntentNode" ||
          e.id.includes("ButtonAdd") ||
          e.id.includes("buttonAdd"))
    ) || null
  );
};

const getChildrenNodes = (nodes, parentId) => {
  return nodes.filter((n) => n.data?.parentData?.id === parentId) || [];
};

const getUpdatedNodePosition = (node, moveChange, childNode) => {
  return {
    ...node,
    position: {
      x:
        moveChange.position.x +
        (node.data.offsetXbutton || node.data.offsetX + 392),
      y:
        moveChange.position.y +
        (node.data.offsetYbutton || node.data.offsetY + 15.3),
    },
    data: {
      ...node.data,
      parentData: {
        ...node.data.parentData,
        position: {
          x:
            moveChange.position.x +
            (node.data.offsetXbutton || node.data.offsetX + 392),
          y:
            moveChange.position.y +
            (node.data.offsetYbutton || node.data.offsetY + 15.3),
        },
      },
    },
  };
};

const getUpdatedChildrenNodePosition = (node, moveChange, childNode) => {
  return {
    ...node,
    position: {
      x:
        moveChange.position.x +
        childNode.data.offsetXbutton +
        node.data.offsetX,
      y: moveChange.position.y + node.data.offsetY,
    },
    data: {
      ...node.data,
      parentData: {
        ...node.data.parentData,
        position: {
          x: moveChange.position.x + childNode.data.offsetXbutton,
          y: moveChange.position.y + childNode.data.offsetYbutton,
        },
      },
    },
  };
};

const FlowComponent = ({
  t,
  actualProject,
  updateContexts,
  updateIntent,
  limits,
  access,
  onNodesUpdate,
  onEdgesUpdate,
  dispatch,
  data,
  handleTabs,
  history,
  i18n,
  location,
  staticContext,
  tReady,
  updateElement,
  contexts = [],
  type,
}) => {
  const {
    setNodes,
    setEdges,
    edges,
    nodes,
    intent,
    setParams,
    setIntent,
    theme,
    onNodesChange,
    onEdgesChange,
    setTheme,
    setContexts,
    updateIntegrations,
    limitsIntegrations,
  } = useFlowNodes();

  const [isReady, setIsReady] = useState(false);
  const [collapsedNodes, setCollapsedNodes] = useState({
    entryNode: false,
    responseNode: false,
    descriptionNode: false,
    extensionsNode: false,
    parameterNode: false,
  });

  useEffect(() => {
    const fetchAndSetIntegrations = async () => {
      try {
        const integrationsData = await fetchIntegrations({
          data: {
            access,
            actualProject,
            limitsIntegrations,
            intent,
          },
          dispatch,
          handleTabs,
          history,
          i18n,
          location,
          t,
          tReady,
        });
        updateIntegrations(integrationsData);
      } catch (error) {
        updateIntegrations([]);
      }
    };
    fetchAndSetIntegrations();
  }, []);

  useEffect(() => {
    const fetchAndSetIntegrations = async () => {
      try {
        const integrationsData = await fetchIntegrations({
          data: {
            access,
            actualProject,
            limitsIntegrations,
            intent,
          },
          dispatch,
          handleTabs,
          history,
          i18n,
          location,
          t,
          tReady,
        });
        updateIntegrations(integrationsData);
      } catch (error) {
        updateIntegrations([]);
      }
    };
    fetchAndSetIntegrations();
  }, []);

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        await fetchData({
          dispatch,
          actualProject,
          access,
          t,
          updateContexts,
          updateIntent,
          limits,
          onNodesUpdate,
          handleTabs,
          history,
          i18n,
          location,
          staticContext,
          tReady,
          data,
          setParams,
          setIntent,
          setNodes,
          setEdges,
          contexts,
          updateElement,
          setContexts,
        });

        setIsReady(true);
      } catch (err) {}
    };

    fetchInitialData();
  }, [
    actualProject,
    access,
    updateIntent,
    dispatch,
    collapsedNodes,
    onNodesUpdate,
    t,
    limits,
    history,
  ]);

  useEffect(() => {
    if (onNodesUpdate) {
      onNodesUpdate(nodes);
    }
    if (onEdgesUpdate) {
      onEdgesUpdate(edges);
    }
  }, [nodes, edges, onNodesUpdate, onEdgesUpdate]);

  const onConnect = useCallback(
    (params) => setEdges((els) => addEdge(params, els)),
    [setEdges]
  );

  if (!isReady) {
    return (
      <div className="cen-init">
        <CircularProgress color="primary" size={70} />
      </div>
    );
  }

  function mapNodeForMove(node, childNode, childrenNode, moveChange) {
    if (node.id === childNode.id) {
      return getUpdatedNodePosition(node, moveChange, childNode);
    }
    if (childrenNode?.some((i) => i.id === node.id)) {
      return getUpdatedChildrenNodePosition(node, moveChange, childNode);
    }
    return node;
  }

  const handleNodesChange = (changes) => {
    onNodesChange(changes);
    const moveChange = changes.find((c) => c.type === "position" && c.position);
    if (!moveChange) return;
    const childNode = getFirstChildNode(nodes, moveChange.id);
    const childrenNode = getChildrenNodes(nodes, moveChange.id);
    if (!childNode) return;
    setNodes((nds) =>
      nds.map((node) => mapNodeForMove(node, childNode, childrenNode, moveChange))
    );
  };

  return (
    <div
      style={{
        width: "100%",
        height: "100vh",
        background: theme === "dark" ? "#1a1a1a" : "var(--color-base100)",
      }}
    >
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={handleNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        fitView={false}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
      >
        <Background
          color={theme === "dark" ? "#555" : "#aaa"}
          variant="dots"
          gap={12}
          size={1}
        />
        <Panel position="top-right" style={{ top: 20, right: 20 }}>
          <ThemeSelector theme={theme} setTheme={setTheme} t={t} />
        </Panel>
        <MiniMap />
        <Controls
          showInteractive
          style={{
            top: 20,
            left: 20,
            background: "white",
            borderRadius: 6,
            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
            padding: 4,
            display: "flex",
            flexDirection: "row",
            gap: 8,
            height: "min-content",
          }}
        />
      </ReactFlow>
      <SidebarFlow />
    </div>
  );
};

export default FlowComponent;
